import { supabase } from '../supabase';
import { RecommendationAlgorithm } from '../../types/index';
import { RECOMMENDATION_CONFIG } from '../recommenders';

// Definir IAlgorithmSelector localmente
interface IAlgorithmSelector {
  determineAlgorithm(userId: string, forcePersonalized?: boolean): Promise<RecommendationAlgorithm>;
}

/**
 * Serviço para selecionar o algoritmo de recomendação mais adequado
 */
export class AlgorithmSelector implements IAlgorithmSelector {
  /**
   * Determina o melhor algoritmo para o usuário
   */
  public async determineAlgorithm(userId: string, forcePersonalized: boolean = false): Promise<RecommendationAlgorithm> {
    try {
      console.log(`Determinando o melhor algoritmo para o usuário ${userId}`);
      
      // Se forcePersonalized = true, pular checagens e usar algoritmo colaborativo ou baseado em conteúdo
      if (forcePersonalized) {
        console.log('Forçando algoritmo personalizado devido ao parâmetro forcePersonalized');
        return await this.selectBestPersonalizedAlgorithm(userId);
      }
      
      // Verificar se o usuário tem jogos suficientes na coleção
      const { count: collectionCount, error: collectionError } = await supabase
        .from('user_games')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);
        
      if (collectionError) {
        console.error('Erro ao contar jogos da coleção:', collectionError);
        return RecommendationAlgorithm.POPULAR;
      }
      
      // Verificar se o usuário tem avaliações suficientes
      const { count: ratingCount, error: ratingError } = await supabase
        .from('user_games')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('rating', 'is', null);
        
      if (ratingError) {
        console.error('Erro ao contar avaliações do usuário:', ratingError);
        return RecommendationAlgorithm.POPULAR;
      }
      
      console.log(`Usuário possui ${collectionCount || 0} jogos na coleção e ${ratingCount || 0} avaliações`);
      
      // Verificar algoritmos que funcionaram bem para o usuário no passado
      const algorithmSuccessMap = await this.getAlgorithmSuccessRates(userId);
      
      // Usuário com poucos jogos e poucas avaliações (novo usuário)
      if ((collectionCount || 0) < RECOMMENDATION_CONFIG.selectorParams.minCollectionSizeForPersonalized && 
          (ratingCount || 0) < RECOMMENDATION_CONFIG.selectorParams.minRatingCountForPersonalized) {
        console.log('Usuário novo com poucos jogos e avaliações. Usando algoritmo POPULAR.');
        return RecommendationAlgorithm.POPULAR;
      }
      
      // Usuário com algumas avaliações mas sem feedback claro sobre algoritmos
      // Neste caso, testar diferentes algoritmos
      if ((ratingCount || 0) >= RECOMMENDATION_CONFIG.selectorParams.minRatingCountForPersonalized && 
          Object.keys(algorithmSuccessMap).length === 0) {
        console.log('Usuário com avaliações, mas sem feedback claro sobre algoritmos. Testando algoritmos personalizados.');
        // Usar pesos da configuração
        return this.selectRandomAlgorithm(
          [
            RecommendationAlgorithm.CONTENT_BASED, 
            RecommendationAlgorithm.COLLABORATIVE, 
            RecommendationAlgorithm.POPULAR
          ],
          [
            RECOMMENDATION_CONFIG.selectorParams.randomSelectionWeights.contentBased,
            RECOMMENDATION_CONFIG.selectorParams.randomSelectionWeights.collaborative,
            RECOMMENDATION_CONFIG.selectorParams.randomSelectionWeights.popular,
          ]
        );
      }
      
      // Usuário com muitas avaliações e jogos na coleção
      if ((ratingCount || 0) >= RECOMMENDATION_CONFIG.selectorParams.minRatingCountForExperienced) {
        console.log('Usuário com muitas avaliações. Preferindo algoritmo COLLABORATIVE.');
        
        // Verificar se temos outros usuários similares suficientes
        const hasEnoughSimilarUsers = await this.hasEnoughSimilarUsers(userId);
        
        if (hasEnoughSimilarUsers) {
          return RecommendationAlgorithm.COLLABORATIVE;
        } else {
          console.log('Não há usuários similares suficientes. Usando CONTENT_BASED como alternativa.');
          return RecommendationAlgorithm.CONTENT_BASED;
        }
      }
      
      // Verificar se temos histórico de sucesso com algum algoritmo
      if (Object.keys(algorithmSuccessMap).length > 0) {
        // Encontrar o algoritmo mais bem sucedido
        let bestAlgorithm = RecommendationAlgorithm.POPULAR;
        let bestRate = 0;
        
        for (const [algorithm, successRate] of Object.entries(algorithmSuccessMap)) {
          if (successRate > bestRate) {
            bestRate = successRate;
            bestAlgorithm = algorithm as RecommendationAlgorithm;
          }
        }
        
        // Se o algoritmo tem uma taxa de sucesso razoável, usá-lo
        if (bestRate >= RECOMMENDATION_CONFIG.selectorParams.minSuccessRateThreshold) {
          console.log(`Usando algoritmo com melhor desempenho histórico: ${bestAlgorithm} (${bestRate.toFixed(2)})`);
          return bestAlgorithm as RecommendationAlgorithm;
        }
      }
      
      // Padrão: baseado em conteúdo para usuários com coleção média
      if ((collectionCount || 0) >= RECOMMENDATION_CONFIG.selectorParams.minCollectionSizeForPersonalized || 
          (ratingCount || 0) >= RECOMMENDATION_CONFIG.selectorParams.minRatingCountForPersonalized) {
        console.log('Usando algoritmo CONTENT_BASED para usuário com coleção média.');
        return RecommendationAlgorithm.CONTENT_BASED;
      }
      
      // Caso contrário, usar popular
      console.log('Nenhuma condição específica encontrada. Usando algoritmo POPULAR.');
      return RecommendationAlgorithm.POPULAR;
    } catch (error) {
      console.error('Erro ao determinar algoritmo:', error);
      return RecommendationAlgorithm.POPULAR;
    }
  }
  
  /**
   * Seleciona o melhor algoritmo personalizado para o usuário
   */
  private async selectBestPersonalizedAlgorithm(userId: string): Promise<RecommendationAlgorithm> {
    try {
      // Verificar se o usuário tem avaliações suficientes para colaborativo
      const { count: ratingCount, error: ratingError } = await supabase
        .from('user_games')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .not('rating', 'is', null);
        
      if (!ratingError && (ratingCount || 0) >= RECOMMENDATION_CONFIG.selectorParams.minRatingCountForCollaborative) {
        // Verificar se temos usuários similares suficientes
        const hasEnoughSimilarUsers = await this.hasEnoughSimilarUsers(userId);
        
        if (hasEnoughSimilarUsers) {
          console.log('Usuário tem avaliações suficientes e usuários similares. Usando COLLABORATIVE.');
          return RecommendationAlgorithm.COLLABORATIVE;
        }
      }
      
      console.log('Usuário não tem requisitos para COLLABORATIVE. Usando CONTENT_BASED.');
      return RecommendationAlgorithm.CONTENT_BASED;
    } catch (error) {
      console.error('Erro ao selecionar algoritmo personalizado:', error);
      return RecommendationAlgorithm.CONTENT_BASED;
    }
  }
  
  /**
   * Verifica se existem usuários similares suficientes
   */
  private async hasEnoughSimilarUsers(userId: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('user_similarities')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gt('similarity_score', RECOMMENDATION_CONFIG.selectorParams.minSimilarityScore)
        .limit(RECOMMENDATION_CONFIG.selectorParams.minSimilarUsers);
        
      if (error) {
        console.error('Erro ao verificar usuários similares:', error);
        return false;
      }
      
      return (count || 0) >= RECOMMENDATION_CONFIG.selectorParams.minSimilarUsers;
    } catch (error) {
      console.error('Erro ao verificar usuários similares:', error);
      return false;
    }
  }
  
  /**
   * Obtém as taxas de sucesso dos algoritmos para o usuário
   */
  private async getAlgorithmSuccessRates(userId: string): Promise<Partial<Record<RecommendationAlgorithm, number>>> {
    try {
      // Obter estatísticas de recomendações com feedback
      const { data, error } = await supabase
        .from('recommendations')
        .select('algorithm, liked')
        .eq('user_id', userId)
        .not('liked', 'is', null)
        .not('algorithm', 'is', null);
        
      if (error || !data) {
        console.error('Erro ao obter estatísticas de algoritmos:', error);
        return {};
      }
      
      // Contar sucessos e totais por algoritmo
      const algorithmStats: Record<string, { success: number; total: number }> = {};
      
      data.forEach(rec => {
        if (!rec.algorithm) return;
        
        if (!algorithmStats[rec.algorithm]) {
          algorithmStats[rec.algorithm] = { success: 0, total: 0 };
        }
        
        algorithmStats[rec.algorithm].total++;
        
        if (rec.liked === true) {
          algorithmStats[rec.algorithm].success++;
        }
      });
      
      // Calcular taxa de sucesso
      const successRates: Partial<Record<RecommendationAlgorithm, number>> = {};
      
      Object.entries(algorithmStats).forEach(([algorithm, stats]) => {
        // Precisamos de pelo menos X amostras para considerar (usar configuração)
        if (stats.total >= RECOMMENDATION_CONFIG.selectorParams.minFeedbackSamplesForSuccessRate) {
          successRates[algorithm as RecommendationAlgorithm] = stats.success / stats.total;
        }
      });
      
      console.log('Taxas de sucesso dos algoritmos:', successRates);
      return successRates;
    } catch (error) {
      console.error('Erro ao calcular taxas de sucesso dos algoritmos:', error);
      return {};
    }
  }
  
  /**
   * Seleciona um algoritmo aleatoriamente com base em pesos
   */
  private selectRandomAlgorithm(algorithms: RecommendationAlgorithm[], weights: number[]): RecommendationAlgorithm {
    if (algorithms.length === 0) return RecommendationAlgorithm.POPULAR;
    if (algorithms.length === 1) return algorithms[0];
    
    // Normalizar pesos se necessário
    if (!weights || weights.length !== algorithms.length) {
      // Pesos iguais para todos
      weights = algorithms.map(() => 1 / algorithms.length);
    }
    
    // Construir array cumulativo
    let cumulative = 0;
    const cumulativeWeights = weights.map(w => cumulative += w);
    
    // Número aleatório entre 0 e 1
    const random = Math.random();
    
    // Encontrar o algoritmo correspondente
    for (let i = 0; i < cumulativeWeights.length; i++) {
      if (random < cumulativeWeights[i]) {
        return algorithms[i];
      }
    }
    
    return algorithms[algorithms.length - 1]; // Último caso
  }
} 